import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import { identity, pipe } from 'ramda';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Field, Form } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import { CohortId } from '../../api/Cohort/types/Cohort';
import authAPI from '../../api/auth';
import { Specialization } from '../../api/specializations';
import { Role } from '../../api/user/Role';
import { VentureDetails } from '../../api/ventures';
import { Status, Venture } from '../../api/ventures/types/Venture';
import { UserContext } from '../../contexts/user-context';
import { useScrollOnValidation } from '../../hooks/useScrollOnValidation';
import { Address, Name } from '../../types';
import {
  apply,
  Either,
  isLeft,
  left,
  parseObject,
  right,
} from '../../utils/Either';
import { fromString, Sentence } from '../../utils/String/Sentence';
import { isUrl } from '../../utils/String/Url';
import { stringToEitherZip, Zip } from '../../utils/String/Zip';
import { formatDateToRFC } from '../../utils/date';
import {
  PastStringDate,
  strToEitherPastStrDate,
} from '../../utils/date/PastStringDate';
import {
  lengthField,
  REASON_FOR_EXIT,
  STATUSES_VENTURES,
  VENTURE_STAGE_OPTIONS,
} from '../../utils/form';
import { isMobile } from '../../utils/functions';
import { removeFalsyValues } from '../../utils/object';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import {
  BeforeUnload,
  Button,
  Dialog,
  FormGroup,
  StickyContent,
  Text,
} from '../common';
import {
  FormComboBox,
  FormDateInput,
  FormNameVenture,
  FormSelect,
  FormSpecializationInput,
  FormVentureTagsInput,
  TextFieldWrapper,
} from './wrappers';

interface VentureDetailsFormProps {
  ventureId?: Venture['id'];
  ventureDetails?: Venture;
  ventureSpecializations?: Specialization['id'][];
  ventureTags?: string[];
  loading?: boolean;
  onSubmit: (parsedFormValues: ParsedFormValues) => Promise<any>;
  isExternal?: boolean;
  cohorts: Array<{
    label: string;
    value: CohortId;
  }>;
}

interface ArchiveFormValues {
  exitReason: string;
  dateOfExit: string;
}

interface ArchiveDialogProps {
  open: boolean;
  initialValues?: ArchiveFormValues;
  setOpen: (value: boolean) => void;
  onSubmit: (values: ArchiveFormValues) => void;
}

interface ActiveDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onSubmit: () => void;
}

interface FormValues {
  ventureName: Name | string;
  url: Sentence<1024> | string | null;
  dateOfEnrollment: PastStringDate | string | null;
  dateOfCreation: PastStringDate | string;
  country: string | Sentence<1024> | null;
  state: string | Sentence<1024> | null;
  city: string | Sentence<1024> | null;
  address: string | Sentence<4096> | null;
  zip: string | Zip | null;
  description: Sentence<250> | string | null;
  specializations: Specialization['id'][];
  ventureTags: string[];
  dateOfExit: string;
  exitReason: string;
  status: Status;
  cohortId: CohortId | 'no-group';
  ventureStage: Sentence<255> | string | null;
  businessModel: Sentence<255> | string | null;
  mentorshipNeeds: Sentence<4096> | string | null;
  matchingNotes: Sentence<4096> | string | null;
}

interface ValidFormValues extends FormValues {
  ventureName: Name;
  url: Sentence<1024> | null;
  dateOfEnrollment: PastStringDate | null;
  dateOfCreation: PastStringDate;
  country: Sentence<1024> | null;
  state: Sentence<1024> | null;
  city: Sentence<1024> | null;
  address: Address | null;
  zip: Zip | null;
  description: Sentence<250> | null;
  specializations: Specialization['id'][];
  ventureTags: string[];
  dateOfExit: string;
  exitReason: string;
  status: Status;
  cohortId: CohortId | 'no-group';
  ventureStage: Sentence<255> | null;
  businessModel: Sentence<255> | null;
  mentorshipNeeds: Sentence<4096> | null;
  matchingNotes: Sentence<4096> | null;
}

const isEnrollmentDateRequired = (status: Status) => status === 'Active';

function getValidForm(form: FormValues): Either<Errors, ValidFormValues> {
  return parseObject<FormValues, ValidFormValues, Errors>(
    {
      ventureName: (v) => (v ? fromString(256, v) : left('Required')),
      url: (v) =>
        v
          ? isUrl(v)
            ? fromString(1024, v)
            : left('Invalid URL')
          : right(null),
      dateOfEnrollment: (v) =>
        !v
          ? isEnrollmentDateRequired(form.status)
            ? left('Required')
            : right(null)
          : strToEitherPastStrDate(v),
      dateOfCreation: strToEitherPastStrDate,
      country: (v) => (v ? fromString(1024, v) : right(null)),
      state: (v) => (v ? fromString(1024, v) : right(null)),
      city: (v) => (v ? fromString(1024, v) : right(null)),
      address: (v) => (v ? fromString(1024, v) : right(null)),
      zip: (v) => (v ? stringToEitherZip(v) : right(null)),
      description: (v) => (v ? fromString(250, v) : right(null)),
      specializations: right,
      ventureTags: right,
      dateOfExit: (v) =>
        !!form.exitReason && !v ? left('Required') : right(v),
      exitReason: (v) =>
        !!form.dateOfExit && !v ? left('Required') : right(v),
      status: right,
      cohortId: right,
      ventureStage: (v) => (v ? fromString(255, v) : right(null)),
      businessModel: (v) => (v ? fromString(255, v) : right(null)),
      mentorshipNeeds: (v) => (v ? fromString(4096, v) : right(null)),
      matchingNotes: (v) => (v ? fromString(4096, v) : right(null)),
    },
    form,
  );
}

export interface ParsedFormValues {
  values: Omit<VentureDetails, 'numOfEmployees' | 'isTech'> & {
    dateOfEnrollment: PastStringDate | null;
  };
  specializations: Specialization['id'][];
  ventureTags: string[];
}

type Errors = {
  [K in keyof FormValues]?: string;
};

const useStyles = makeStyles((theme) => ({
  sectionBlock: {
    '& + &': {
      marginTop: 56,
    },
  },
  sectionTitle: {
    marginBottom: 32,
  },
  mainFormBlock: {
    width: '100%',

    [theme.breakpoints.up(850)]: {
      width: 560,
    },
  },
  actionsBlock: {
    marginTop: 56,
  },
  descriptionField: {
    minHeight: 64,
  },
  checkbox: {
    paddingLeft: 0,
  },
  checkboxLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
    },
  },
}));

const useDialogStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 32,
    gap: 16,
  },
}));

const validateForm = (values: FormValues) => {
  const errors = getValidForm(values);
  return isLeft(errors) ? errors.value : {};
};

function getInitialValues(
  ventureDetails?: VentureDetails,
  specializations: Specialization['id'][] = [],
  ventureTags: string[] = [],
): FormValues {
  const country = ventureDetails?.country || '';

  const nonFiltredInitialData: FormValues = {
    ventureName: ventureDetails?.ventureName || '',
    url: ventureDetails?.url || '',
    dateOfEnrollment: ventureDetails?.dateOfEnrollment || '',
    dateOfCreation:
      ventureDetails?.dateOfCreation || formatDateToRFC(new Date()),
    country,
    state: ventureDetails?.state || '',
    city: ventureDetails?.city || '',
    address: ventureDetails?.address || '',
    zip: ventureDetails?.zip || '',
    description: ventureDetails?.description || '',
    specializations,
    ventureTags,
    dateOfExit: ventureDetails?.dateOfExit || '',
    exitReason: ventureDetails?.exitReason || '',
    status: ventureDetails?.status || STATUSES_VENTURES[2].value,
    cohortId: ventureDetails?.cohortId || 'no-group',
    ventureStage: ventureDetails?.ventureStage || '',
    businessModel: ventureDetails?.businessModel || '',
    mentorshipNeeds: ventureDetails?.mentorshipNeeds || '',
    matchingNotes: ventureDetails?.matchingNotes || '',
  };

  return {
    ...removeFalsyValues(nonFiltredInitialData),
    dateOfEnrollment: ventureDetails?.dateOfEnrollment || '',
    dateOfExit: ventureDetails?.dateOfExit || '',
  };
}

function getParsedValues(formValues: ValidFormValues): ParsedFormValues {
  return {
    values: {
      ventureName: formValues.ventureName,
      url: formValues.url,
      dateOfEnrollment: formValues.dateOfEnrollment || null,
      dateOfCreation: formValues.dateOfCreation,
      country: formValues.country || null,
      state: formValues.state || null,
      city: formValues.city || null,
      address: formValues.address || null,
      zip: formValues.zip || null,
      description: formValues.description || null,
      dateOfExit: formValues.dateOfExit || '',
      exitReason: formValues.exitReason || '',
      status: formValues?.status || STATUSES_VENTURES[2].value,
      cohortId:
        formValues?.cohortId === 'no-group' ? null : formValues?.cohortId,
      ventureStage: formValues.ventureStage || null,
      businessModel: formValues.businessModel || null,
      mentorshipNeeds: formValues.mentorshipNeeds || null,
      matchingNotes: formValues.matchingNotes || null,
    },
    specializations: formValues.specializations,
    ventureTags: formValues.ventureTags,
  };
}

function ArchiveDialog({
  open,
  initialValues,
  setOpen,
  onSubmit,
}: ArchiveDialogProps) {
  const classes = useDialogStyles();

  return (
    <Dialog
      title='Reason for archiving'
      width={480}
      contentRenderer={() => (
        <Form
          onSubmit={onSubmit}
          initialValues={initialValues}
          keepDirtyOnReinitialize
          render={(formProps) => (
            <form noValidate>
              <FormGroup mobile={isMobile()}>
                <Field<string>
                  testid='venture-details-exit-reason'
                  name='exitReason'
                  component={FormSelect}
                  label='Reason for exit'
                  options={REASON_FOR_EXIT}
                  onChange={() => {
                    if (!formProps?.values?.dateOfExit) {
                      const currentDate = new Date();
                      const currentDateStr = formatDateToRFC(currentDate);
                      formProps.form.change('dateOfExit', currentDateStr);
                    }
                  }}
                />
                <Field<string>
                  testid='venture-details-date-exit'
                  name='dateOfExit'
                  component={FormDateInput}
                  label='Exit Date'
                  editable
                  parse={(value) => {
                    try {
                      if (value) {
                        return formatDateToRFC(value);
                      }
                      return '';
                    } catch (e: any) {
                      return 'invalid';
                    }
                  }}
                />
              </FormGroup>
              <div className={classes.actions}>
                <Button
                  variant='outlined'
                  onClick={() => setOpen(false)}
                  data-testid='button-cancel'
                >
                  Cancel
                </Button>
                <Button
                  data-testid='button-success'
                  onClick={formProps.handleSubmit}
                >
                  Ok
                </Button>
              </div>
            </form>
          )}
        />
      )}
      open={open}
      setOpen={setOpen}
    />
  );
}

function ActiveDialog({ open, setOpen, onSubmit }: ActiveDialogProps) {
  const classes = useDialogStyles();

  return (
    <Dialog
      title='Please Note'
      width={480}
      contentRenderer={() => (
        <div>
          <Text variant='normal'>
            You are changing the venture's status to Active. When you save the
            change:
            <ul>
              <li>
                1. All linked founders will be updated to an Active status
              </li>
              <li>2. System users will be created for linked founders</li>
              <li>
                3. We will send invites to founders' emails to join the program
              </li>
            </ul>
            <br />
            If you want stop the process, press on Cancel now.
          </Text>
          <div className={classes.actions}>
            <Button
              variant='outlined'
              onClick={() => setOpen(false)}
              data-testid='button-cancel'
            >
              Cancel
            </Button>
            <Button data-testid='button-success' onClick={onSubmit}>
              Ok
            </Button>
          </div>
        </div>
      )}
      open={open}
      setOpen={setOpen}
    />
  );
}

function VentureDetailsForm({
  ventureDetails,
  ventureSpecializations,
  ventureTags,
  loading = false,
  onSubmit,
  isExternal = false,
  cohorts,
}: VentureDetailsFormProps) {
  const classes = useStyles();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const setSubmitValidationFailed = useScrollOnValidation();
  const [fieldsValidation, setFieldsValidation] = useState<{ name: boolean }>();
  const [isAsyncValidating, setIsAsyncValidating] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showActiveDialog, setShowActiveDialog] = useState(false);
  const {
    user,
    hasAccessToAction: checkAccess,
    hasRole,
  } = useContext(UserContext);
  const hasAccessToAction = useMemo(
    () => (isExternal ? () => true : checkAccess),
    [checkAccess, isExternal],
  );

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );
  const anchor = useMemo(() => searchParams?.anchor, [searchParams?.anchor]);

  const handleSubmit = async (values: FormValues) => {
    if (isAsyncValidating) {
      return;
    }

    try {
      setIsAsyncValidating(true);
      const duplicateVenture = await authAPI.checkVentureNameValidation({
        ventureName: values.ventureName,
        tenantId: user?.id || '',
      });

      if (
        duplicateVenture !== null &&
        (!ventureDetails || duplicateVenture.id !== ventureDetails.id)
      ) {
        enqueueSnackbar(
          'The venture name you have provided already exists on our platform. Please reach out to the admin to address the problem.',
          {
            variant: 'error',
          },
        );
        return;
      }

      await pipe(
        getValidForm,
        apply(identity, pipe(getParsedValues, onSubmit)),
      )(values);
      setIsAsyncValidating(false);
    } catch (error) {
      setIsAsyncValidating(false);
    }
  };

  const initialValues = getInitialValues(
    ventureDetails,
    ventureSpecializations,
    ventureTags,
  );

  const handleFieldsValidation = useCallback(
    (field: 'email' | 'name') => (valid: boolean) => {
      setFieldsValidation(
        (prevValidations) =>
        ({
          ...(prevValidations ? prevValidations : {}),
          [field]: valid,
        } as { name: boolean }),
      );
    },
    [],
  );

  useEffect(() => {
    try {
      if (anchor && typeof anchor === 'string') {
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [anchor]);

  return (
    <div>
      <Form
        validate={validateForm}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        keepDirtyOnReinitialize
        render={(formProps) => {
          setSubmitValidationFailed(
            formProps.submitFailed &&
            !formProps.dirtySinceLastSubmit &&
            !formProps.submitting,
          );
          return (
            <>
              <BeforeUnload
                when={formProps.dirty && !loading}
                title='Leave the page'
                body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
                disabled={loading}
                confirmButtonRenderer={({ onConfirm }) => (
                  <Button
                    variant='outlined'
                    onClick={async () => {
                      await formProps.handleSubmit();
                      onConfirm();
                    }}
                    disabled={
                      loading || !formProps.valid || !fieldsValidation?.name
                    }
                  >
                    {loading ? (
                      <CircularProgress size={24} color='inherit' />
                    ) : (
                      'Save the changes'
                    )}
                  </Button>
                )}
              />
              <div
                className={classes.mainFormBlock}
                data-testid='venture-details-form'
              >
                <form noValidate>
                  <div className={classes.sectionBlock}>
                    <ActiveDialog
                      open={showActiveDialog}
                      setOpen={setShowActiveDialog}
                      onSubmit={() => {
                        formProps.form.change('status', 'Active');
                        if (!formProps.values.dateOfEnrollment) {
                          formProps.form.change(
                            'dateOfEnrollment',
                            formatDateToRFC(new Date()),
                          );
                        }
                        setShowActiveDialog(false);
                      }}
                    />
                    <ArchiveDialog
                      initialValues={{
                        exitReason: formProps?.values?.exitReason,
                        dateOfExit: formProps?.values?.dateOfExit,
                      }}
                      open={showArchiveDialog}
                      setOpen={setShowArchiveDialog}
                      onSubmit={(values) => {
                        formProps.form.change('exitReason', values.exitReason);
                        formProps.form.change('dateOfExit', values.dateOfExit);
                        setShowArchiveDialog(false);
                      }}
                    />
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-venture-name'
                        name='ventureName'
                        component={FormNameVenture}
                        label='Venture Name*'
                        onValid={handleFieldsValidation('name')}
                        inputProps={{
                          readOnly: !checkAccess('venture.details.update'),
                        }}
                        error={
                          (formProps?.touched?.ventureName ||
                            formProps?.submitFailed) &&
                          formProps?.errors?.ventureName
                        }
                      />

                      <Field<string>
                        testid='venture-details-venture-status'
                        name='status'
                        component={FormSelect}
                        label='Venture Status'
                        options={STATUSES_VENTURES}
                        readOnly={
                          !(hasRole(Role.Admin) || hasRole(Role.Manager))
                        }
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                          const value = e.target.value;
                          if (value === 'Active') {
                            e.target.value = formProps?.values?.status || '';
                            setShowActiveDialog(true);
                          } else if (value === 'Archived') {
                            setShowArchiveDialog(true);
                          }
                        }}
                      />
                    </FormGroup>

                    <FormGroup mobile={isMobile()}>
                      <Field<string>
                        testid='venture-details-date-creation'
                        name='dateOfCreation'
                        component={FormDateInput}
                        label='Application Date*'
                        editable
                        isReadOnly={!checkAccess('venture.details.update')}
                        parse={(value) => {
                          try {
                            if (value) {
                              return formatDateToRFC(value);
                            }
                            return '';
                          } catch (e: any) {
                            return 'invalid';
                          }
                        }}
                      />
                      <Field<string>
                        testid='venture-details-date-enrollemnt'
                        name='dateOfEnrollment'
                        component={FormDateInput}
                        label={`Date Of Enrollment${isEnrollmentDateRequired(formProps.values.status)
                          ? '*'
                          : ''
                          }`}
                        maxDate={new Date()}
                        isReadOnly={!checkAccess('venture.details.update')}
                        editable
                        parse={(value) => {
                          try {
                            if (value) {
                              return formatDateToRFC(value);
                            }
                            return '';
                          } catch (e: any) {
                            return 'invalid';
                          }
                        }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Field<string[]>
                        data-testid='venture-details-tags'
                        name='ventureTags'
                        component={FormVentureTagsInput}
                        label='Issues'
                        disabled={!ventureTags}
                        readOnly={!checkAccess('venture.details.update')}
                      />
                    </FormGroup>

                    {!hasRole(Role.Founder) && (
                      <FormGroup>
                        <Field<CohortId | 'no-group'>
                          testid='venture-details-venture-cohort'
                          name='cohortId'
                          component={FormSelect}
                          label='Venture Group'
                          options={[
                            {
                              value: 'no-group',
                              label: 'No group',
                            },
                            ...cohorts,
                          ]}
                          readOnly={
                            !(hasRole(Role.Admin) || hasRole(Role.Manager))
                          }
                        />
                      </FormGroup>
                    )}

                    {!isExternal && !hasRole(Role.Founder) && (
                      <FormGroup mobile={isMobile()}>
                        <Field<string>
                          testid='venture-details-exit-reason'
                          name='exitReason'
                          component={FormSelect}
                          label='Reason for exit'
                          options={REASON_FOR_EXIT}
                          readOnly={
                            !hasAccessToAction('venture.details.update')
                          }
                          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                            const value = event.target.value;
                            if (!!value && !formProps?.values?.dateOfExit) {
                              const currentDate = new Date();
                              const currentDateStr =
                                formatDateToRFC(currentDate);
                              formProps.form.change(
                                'dateOfExit',
                                currentDateStr,
                              );
                            } else if (
                              !value &&
                              !!formProps?.values?.dateOfExit
                            ) {
                              formProps.form.change('dateOfExit', '');
                            }
                          }}
                        />
                        <Field<string>
                          testid='venture-details-date-exit'
                          name='dateOfExit'
                          component={FormDateInput}
                          label='Exit Date'
                          editable
                          isReadOnly={
                            !hasAccessToAction('venture.details.update')
                          }
                          parse={(value) => {
                            try {
                              if (value) {
                                return formatDateToRFC(value);
                              }
                              return '';
                            } catch (e: any) {
                              return 'invalid';
                            }
                          }}
                        />
                      </FormGroup>
                    )}
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-website'
                        name='url'
                        component={TextFieldWrapper}
                        label='Website'
                        placeholder='https://www.yourwebsite.com'
                        InputProps={{
                          inputProps: {
                            maxLength: lengthField.url,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup id='description'>
                      <Field<string>
                        testid='venture-details-description'
                        name='description'
                        component={TextFieldWrapper}
                        label='Description'
                        multiline
                        InputProps={{
                          inputProps: {
                            className: classes.descriptionField,
                            maxLength: lengthField.description,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup id='specializations'>
                      <Field<Specialization['id'][]>
                        data-testid='venture-details-specializations'
                        name='specializations'
                        component={FormSpecializationInput}
                        label='Specialization'
                        disabled={!ventureSpecializations}
                        readOnly={
                          !hasAccessToAction(
                            'venture.details.additional.update',
                          )
                        }
                      />
                    </FormGroup>
                    <FormGroup mobile={isMobile()}>
                      <Field<string>
                        testid='venture-details-venture-stage'
                        name='ventureStage'
                        component={FormComboBox}
                        label='Venture Stage'
                        options={[...VENTURE_STAGE_OPTIONS]}
                        readOnly={
                          !hasAccessToAction(
                            'venture.details.additional.update',
                          )
                        }
                      />
                      <Field<string>
                        testid='venture-details-business-model'
                        name='businessModel'
                        component={TextFieldWrapper}
                        label='Business Model'
                        InputProps={{
                          inputProps: {
                            maxLength: lengthField.businessModel,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-mentorship-needs'
                        name='mentorshipNeeds'
                        component={TextFieldWrapper}
                        label='Mentorship Needs'
                        multiline
                        InputProps={{
                          inputProps: {
                            className: classes.descriptionField,
                            maxLength: lengthField.mentorshipNeeds,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-matching-notes'
                        name='matchingNotes'
                        component={TextFieldWrapper}
                        label='Matching Notes'
                        multiline
                        InputProps={{
                          inputProps: {
                            className: classes.descriptionField,
                            maxLength: lengthField.matchingNotes,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                  </div>
                  <div className={classes.sectionBlock}>
                    <Typography className={classes.sectionTitle} variant='h3'>
                      Address
                    </Typography>
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-address'
                        name='address'
                        component={TextFieldWrapper}
                        label='Address'
                        InputProps={{
                          inputProps: {
                            maxLength: lengthField.address,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-city'
                        name='city'
                        component={TextFieldWrapper}
                        label='City'
                        InputProps={{
                          inputProps: {
                            maxLength: lengthField.city,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-state'
                        name='state'
                        component={TextFieldWrapper}
                        label='State'
                        InputProps={{
                          inputProps: {
                            maxLength: lengthField.state,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field<string>
                        testid='venture-details-zip'
                        name='zip'
                        component={TextFieldWrapper}
                        label='Zip code'
                        InputProps={{
                          inputProps: {
                            maxLength: lengthField.zip,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Field<string>
                        data-testid='venture-details-button-submit'
                        name='country'
                        component={TextFieldWrapper}
                        label='Country'
                        InputProps={{
                          inputProps: {
                            maxLength: lengthField.country,
                            readOnly: !hasAccessToAction(
                              'venture.details.additional.update',
                            ),
                          },
                        }}
                      />
                    </FormGroup>
                  </div>
                  {(hasAccessToAction('venture.details.update') ||
                    hasAccessToAction('venture.details.additional.update')) && (
                      <div className={classes.actionsBlock}>
                        <StickyContent>
                          <Button
                            onClick={formProps.handleSubmit}
                            className={CLASS_TRACKING.INTERNAL_ACTION}
                            disabled={loading}
                            startIcon={<CheckIcon />}
                          >
                            {loading ? (
                              <CircularProgress size={24} color='inherit' />
                            ) : (
                              'Save'
                            )}
                          </Button>
                        </StickyContent>
                      </div>
                    )}
                </form>
              </div>
            </>
          );
        }}
      />
    </div>
  );
}

export default VentureDetailsForm;
